import { Inbox, Rows3 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Badge, Button } from "@onemileauto/ui";
import type { AggregatedThreadItem } from "@onemileauto/shared-types";
import { useAggregatedThreads } from "../../hooks/use-aggregated-threads";
import { EmptyState } from "./empty-state";
import { displayParticipants, formatRelativeTime, initialFor } from "../../lib/format";

interface AggregateThreadListProps {
  folderSlug: string;
}

/**
 * "All Inboxes" — the cross-mailbox view (Phase 5). Deliberately read-only/
 * navigational: no swipe-to-archive, no per-row unread toggle. Tapping a
 * thread opens it inside its OWN mailbox (see the Link below, which sets
 * the `mailbox` search param) — that's where reply/archive/etc. actually
 * live, fully reusing the already-verified per-mailbox thread detail view
 * rather than re-implementing write actions against N mailboxes' worth of
 * folder ids here. Kept intentionally simple over a fully unified inbox per
 * this app's non-technical-user, avoid-over-building stance — see
 * docs/architecture/mail-architecture.md's Phase 5 addendum.
 */
export function AggregateThreadList({ folderSlug }: AggregateThreadListProps) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useAggregatedThreads(
    folderSlug,
    true,
  );

  if (isLoading) {
    return <div className="px-4 py-6 text-center text-sm text-muted">Loading&hellip;</div>;
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <p className="text-sm text-danger">Couldn&rsquo;t load your mailboxes.</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const threads = data?.pages.flatMap((p) => p.threads) ?? [];

  if (threads.length === 0) {
    return (
      <EmptyState
        icon={Rows3}
        title="No mail across your mailboxes"
        description="New messages in any mailbox you can see will show up here."
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div>
        {threads.map((thread) => (
          <AggregateThreadListItem key={`${thread.mailboxId}:${thread.threadId}`} thread={thread} folderSlug={folderSlug} />
        ))}
      </div>
      {hasNextPage && (
        <div className="flex justify-center py-4">
          <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function AggregateThreadListItem({ thread, folderSlug }: { thread: AggregatedThreadItem; folderSlug: string }) {
  const unread = thread.unread;

  return (
    <Link
      to="/$threadId"
      params={{ threadId: thread.threadId }}
      search={(prev) => ({ ...prev, mailbox: thread.mailboxId, folder: folderSlug })}
      className="flex items-start gap-3 border-b border-border py-3 pl-4 pr-4 transition-colors hover:bg-parchment-dim"
    >
      <div
        className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          unread ? "bg-rust text-parchment" : "bg-parchment-dim text-muted"
        }`}
        aria-hidden
      >
        {initialFor(thread.participants, thread.mailboxAddress)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className={`truncate text-[15px] ${unread ? "font-semibold text-charcoal" : "font-medium text-charcoal/90"}`}>
            {displayParticipants(thread.participants, thread.mailboxAddress)}
          </p>
          <span className={`shrink-0 text-xs ${unread ? "font-semibold text-rust-deep" : "text-muted"}`}>
            {formatRelativeTime(thread.lastMessageAt)}
          </span>
        </div>
        <p className={`truncate text-sm ${unread ? "font-medium text-charcoal" : "text-charcoal/80"}`}>
          {thread.subject || "(no subject)"}
        </p>
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-sm text-muted">{thread.snippet}</p>
          <Badge variant="muted" className="shrink-0 gap-1">
            <Inbox size={11} />
            {thread.mailboxDisplayName}
          </Badge>
        </div>
      </div>
    </Link>
  );
}
